import { useCallback, useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Video, Send, Trash2, RefreshCw, Link2 } from "lucide-react";

const resolveUrl = (url) => (url?.startsWith("/api/files/") ? `${BACKEND_URL}${url}` : url);

const TABS = [
  { id: "compose", label: "Compose" },
  { id: "assets", label: "Assets" },
  { id: "connections", label: "Connections" },
  { id: "history", label: "History" },
];

const PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "x", label: "X" },
  { id: "tiktok", label: "TikTok" },
];

const inputCls =
  "w-full bg-transparent border px-4 py-3 text-sm text-bone placeholder:text-muted-bone outline-none";
const borderStyle = { borderColor: "var(--rs-border)" };
const btnPrimary =
  "px-6 py-3 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition disabled:opacity-40";

const AdminMarketing = () => {
  const [tab, setTab] = useState("compose");
  const [products, setProducts] = useState([]);
  const [connections, setConnections] = useState(null);
  const [assets, setAssets] = useState([]);
  const [posts, setPosts] = useState([]);
  const [autoVideo, setAutoVideo] = useState(null);

  // Compose state
  const [caption, setCaption] = useState("");
  const [captionProduct, setCaptionProduct] = useState("");
  const [captionPlatform, setCaptionPlatform] = useState("instagram");
  const [captionTheme, setCaptionTheme] = useState("");
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoPreset, setVideoPreset] = useState("reel");
  const [videoProductId, setVideoProductId] = useState("");
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [posting, setPosting] = useState(false);

  // X credentials form
  const [xForm, setXForm] = useState({ api_key: "", api_secret: "", access_token: "", access_token_secret: "" });
  const [savingX, setSavingX] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/marketing/connections").then((r) => setConnections(r.data)).catch(() => {});
    api.get("/admin/marketing/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/admin/marketing/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/admin/marketing/auto-video/status").then((r) => setAutoVideo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const generateCaption = async () => {
    setGeneratingCaption(true);
    try {
      const { data } = await api.post("/admin/marketing/generate-caption", {
        product_id: captionProduct || null,
        platform: captionPlatform,
        theme: captionTheme || null,
      });
      setCaption(data.caption);
      toast.success("Caption drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption generation failed");
    } finally {
      setGeneratingCaption(false);
    }
  };

  const generateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    try {
      const { data } = await api.post("/admin/marketing/generate-image", { prompt: imagePrompt });
      setAssets((prev) => [data, ...prev]);
      setSelectedAsset(data);
      toast.success("Image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateVideo = async () => {
    // Either free-form prompt OR a product must be present.
    if (!videoPrompt.trim() && !videoProductId) {
      toast.error("Type a prompt or pick a product first");
      return;
    }
    setGeneratingVideo(true);
    try {
      const { data } = await api.post("/admin/marketing/generate-video", {
        prompt: videoPrompt || undefined,
        preset: videoPreset,
        product_id: videoProductId || undefined,
      });
      setAssets((prev) => [data, ...prev]);
      toast.success("Video queued — Sora is filming. Check Assets in a few minutes.");
      setVideoProductId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video generation failed");
    } finally {
      setGeneratingVideo(false);
    }
  };

  const runAutoVideoNow = async () => {
    try {
      await api.post("/admin/marketing/auto-video/run-now");
      toast.success("Auto-video queued — new clip will appear in Assets in a few minutes.");
      // Refresh status a moment later
      setTimeout(() => {
        api.get("/admin/marketing/auto-video/status").then((r) => setAutoVideo(r.data)).catch(() => {});
        api.get("/admin/marketing/assets").then((r) => setAssets(r.data)).catch(() => {});
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue auto-video");
    }
  };

  const togglePlatform = (id) =>
    setSelectedPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const submitPost = async () => {
    if (!caption.trim() || selectedPlatforms.length === 0) return;
    setPosting(true);
    try {
      await api.post("/admin/marketing/post", {
        platforms: selectedPlatforms,
        caption,
        asset_path: selectedAsset?.path || null,
      });
      toast.success("Post queued — publishing in the background");
      setTab("history");
      setTimeout(() => api.get("/admin/marketing/posts").then((r) => setPosts(r.data)), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  const connectPlatform = async (platform) => {
    try {
      const { data } = await api.get(`/admin/marketing/${platform}/auth-url`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${platform} not configured yet`);
    }
  };

  const disconnect = async (platform) => {
    await api.delete(`/admin/marketing/${platform}/disconnect`);
    toast.success("Disconnected");
    loadAll();
  };

  const saveXCredentials = async () => {
    setSavingX(true);
    try {
      const { data } = await api.post("/admin/marketing/x/credentials", xForm);
      toast.success(`Connected as @${data.handle}`);
      setXForm({ api_key: "", api_secret: "", access_token: "", access_token_secret: "" });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "X credentials rejected");
    } finally {
      setSavingX(false);
    }
  };

  const deleteAsset = async (assetId) => {
    await api.delete(`/admin/marketing/assets/${assetId}`);
    setAssets((prev) => prev.filter((a) => a.asset_id !== assetId));
    if (selectedAsset?.asset_id === assetId) setSelectedAsset(null);
  };

  const statusPill = (status) => {
    const colors = {
      posted: "#2f6f4f",
      completed: "#2f6f4f",
      ready: "#2f6f4f",
      posting: "#8a6d1a",
      generating: "#8a6d1a",
      queued: "#555",
      failed: "var(--rs-oxblood)",
    };
    return (
      <span
        className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
        style={{ background: colors[status] || "#555" }}
      >
        {status}
      </span>
    );
  };

  return (
    <div data-testid="admin-marketing">
      <div className="overline mb-3">Marketing Studio</div>
      <h1 className="font-serif-display text-5xl text-bone mb-10">Whisper louder.</h1>

      <div className="flex gap-6 border-b mb-10" style={borderStyle}>
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`marketing-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`pb-4 text-xs uppercase tracking-[0.22em] transition ${
              tab === t.id ? "text-bone border-b-2" : "text-muted-bone hover:text-bone"
            }`}
            style={tab === t.id ? { borderColor: "var(--rs-oxblood)" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-8">
            <div className="border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              <div className="overline flex items-center gap-2"><Sparkles size={14} /> AI Caption</div>
              <div className="grid grid-cols-2 gap-3">
                <select
                  data-testid="caption-product-select"
                  value={captionProduct}
                  onChange={(e) => setCaptionProduct(e.target.value)}
                  className={inputCls}
                  style={{ ...borderStyle, background: "var(--rs-surface)" }}
                >
                  <option value="">No product — brand post</option>
                  {products.map((p) => (
                    <option key={p.product_id} value={p.product_id}>{p.name}</option>
                  ))}
                </select>
                <select
                  data-testid="caption-platform-select"
                  value={captionPlatform}
                  onChange={(e) => setCaptionPlatform(e.target.value)}
                  className={inputCls}
                  style={{ ...borderStyle, background: "var(--rs-surface)" }}
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
              <input
                data-testid="caption-theme-input"
                value={captionTheme}
                onChange={(e) => setCaptionTheme(e.target.value)}
                placeholder="Theme (optional) — e.g. Valentine's, slow nights"
                className={inputCls}
                style={borderStyle}
              />
              <button
                data-testid="generate-caption-btn"
                onClick={generateCaption}
                disabled={generatingCaption}
                className={btnPrimary}
                style={{ background: "var(--rs-oxblood)" }}
              >
                {generatingCaption ? "Drafting…" : "Generate caption"}
              </button>
              <textarea
                data-testid="caption-textarea"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={6}
                placeholder="Caption appears here — or write your own."
                className={inputCls}
                style={borderStyle}
              />
            </div>

            <div className="border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              <div className="overline flex items-center gap-2"><ImageIcon size={14} /> AI Image (Nano Banana)</div>
              <input
                data-testid="image-prompt-input"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="e.g. Silk robe draped over emerald marble, candlelight"
                className={inputCls}
                style={borderStyle}
              />
              <button
                data-testid="generate-image-btn"
                onClick={generateImage}
                disabled={generatingImage || !imagePrompt.trim()}
                className={btnPrimary}
                style={{ background: "var(--rs-oxblood)" }}
              >
                {generatingImage ? "Rendering…" : "Generate image"}
              </button>
            </div>

            <div className="border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              <div className="overline flex items-center gap-2"><Video size={14} /> AI Video (Sora 2)</div>

              {/* Product picker — "Generate for this product" writes a cinematic
                  prompt automatically. Optional: leave blank + type free-form. */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/70 mb-2">
                  For a product (optional — auto-writes the prompt)
                </div>
                <select
                  data-testid="video-product-picker"
                  value={videoProductId}
                  onChange={(e) => setVideoProductId(e.target.value)}
                  className={`${inputCls} cursor-pointer`}
                  style={borderStyle}
                >
                  <option value="" style={{ background: "var(--rs-bg)" }}>
                    — Free-form prompt only —
                  </option>
                  {(products || []).slice(0, 300).map((p) => (
                    <option key={p.product_id} value={p.product_id} style={{ background: "var(--rs-bg)" }}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <input
                data-testid="video-prompt-input"
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                placeholder={videoProductId
                  ? "Optional flavour direction — 'add rose petals falling', 'moody thunder'"
                  : "e.g. Slow pan across gold filigree and black silk"}
                className={inputCls}
                style={borderStyle}
              />

              {/* Aspect / duration presets — one button = valid config for a platform. */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/70 mb-2">
                  Format
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { key: "reel", label: "IG / TikTok Reel", ratio: "9:16 · 8s" },
                    { key: "story", label: "Long Story", ratio: "9:16 · 12s" },
                    { key: "feed", label: "IG Feed Loop", ratio: "1:1 · 4s" },
                    { key: "landscape", label: "YouTube / X", ratio: "16:9 · 8s" },
                  ].map((p) => (
                    <button
                      key={p.key}
                      data-testid={`video-preset-${p.key}`}
                      onClick={() => setVideoPreset(p.key)}
                      className={`p-3 text-left border transition ${videoPreset === p.key ? "text-bone" : "text-muted-bone hover:text-bone hover:bg-white/5"}`}
                      style={videoPreset === p.key
                        ? { background: "var(--rs-oxblood)", borderColor: "var(--rs-oxblood)" }
                        : borderStyle}
                    >
                      <div className="text-xs uppercase tracking-widest">{p.label}</div>
                      <div className="text-[10px] text-muted-bone/70 mt-0.5">{p.ratio}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="generate-video-btn"
                onClick={generateVideo}
                disabled={generatingVideo || (!videoPrompt.trim() && !videoProductId)}
                className={`${btnPrimary} w-full`}
                style={{ background: "var(--rs-oxblood)" }}
              >
                {generatingVideo ? "Queuing…" : "Generate cinematic video"}
              </button>

              <p className="text-xs text-muted-bone">Videos render in the background (2–5 min). Find them under Assets. Every clip inherits the Ram Shack dark-luxury style — candlelight, marble, slow camera, tasteful.</p>
            </div>

            {/* ────────────── Auto-Video Schedule ────────────── */}
            <div
              data-testid="auto-video-panel"
              className="border p-6 space-y-3"
              style={{ ...borderStyle, background: "var(--rs-surface)" }}
            >
              <div className="flex items-center justify-between">
                <div className="overline flex items-center gap-2">
                  <Sparkles size={14} /> Auto-Video · twice a day
                </div>
                <button
                  data-testid="auto-video-run-now"
                  onClick={runAutoVideoNow}
                  className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border text-bone hover:bg-white/5 transition"
                  style={borderStyle}
                >
                  Run now
                </button>
              </div>
              <p className="text-xs text-muted-bone">
                Two cinematic clips render every day at random times inside a morning + evening window.
                Each pick rotates between a random product, a best-seller, and a fresh arrival — auto-posted
                to your connected socials.
              </p>
              <div className="grid grid-cols-2 gap-3 pt-1">
                {["morning", "evening"].map((slot) => {
                  const info = autoVideo?.slots?.[slot];
                  const next = info?.next_run_utc ? new Date(info.next_run_utc) : null;
                  const last = info?.last_run;
                  return (
                    <div
                      key={slot}
                      data-testid={`auto-video-slot-${slot}`}
                      className="border p-3"
                      style={borderStyle}
                    >
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/80 mb-1">
                        {slot}
                      </div>
                      <div className="text-sm text-bone">
                        {next
                          ? next.toLocaleString(undefined, {
                              weekday: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </div>
                      <div className="text-[10px] text-muted-bone/70 mt-1">
                        {last?.ran_at
                          ? `Last: ${new Date(last.ran_at).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}${last.status ? ` · ${last.status}` : ""}`
                          : "No runs yet"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* ────────────────────────────────────────────────── */}
          </div>

          <div className="space-y-8">
            <div className="border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              <div className="overline">Attached asset</div>
              {selectedAsset ? (
                <div className="flex items-center gap-4">
                  {selectedAsset.kind === "image" ? (
                    <img src={resolveUrl(selectedAsset.url)} alt="asset" className="w-24 h-24 object-cover border" style={borderStyle} />
                  ) : (
                    <video src={resolveUrl(selectedAsset.url)} className="w-24 h-24 object-cover border" style={borderStyle} muted />
                  )}
                  <div className="flex-1">
                    <div className="text-sm text-bone">{selectedAsset.kind}</div>
                    <div className="text-xs text-muted-bone line-clamp-2">{selectedAsset.prompt}</div>
                  </div>
                  <button
                    data-testid="detach-asset-btn"
                    onClick={() => setSelectedAsset(null)}
                    className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
                  >
                    Detach
                  </button>
                </div>
              ) : (
                <p className="text-sm text-muted-bone">No asset attached. Generate one, or pick from the Assets tab.</p>
              )}
            </div>

            <div className="border p-6 space-y-5" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              <div className="overline flex items-center gap-2"><Send size={14} /> Publish</div>
              <div className="flex flex-wrap gap-3">
                {PLATFORMS.map((p) => {
                  const conn = connections?.[p.id === "facebook" || p.id === "instagram" ? "meta" : p.id];
                  const connected = conn?.connected;
                  return (
                    <button
                      key={p.id}
                      data-testid={`platform-toggle-${p.id}`}
                      onClick={() => togglePlatform(p.id)}
                      className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${
                        selectedPlatforms.includes(p.id) ? "text-bone" : "text-muted-bone"
                      } ${!connected ? "opacity-50" : ""}`}
                      style={
                        selectedPlatforms.includes(p.id)
                          ? { background: "var(--rs-oxblood)", borderColor: "var(--rs-oxblood)" }
                          : borderStyle
                      }
                    >
                      {p.label}
                      {!connected && " ·off"}
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="submit-post-btn"
                onClick={submitPost}
                disabled={posting || !caption.trim() || selectedPlatforms.length === 0}
                className={`w-full ${btnPrimary}`}
                style={{ background: "var(--rs-oxblood)" }}
              >
                {posting ? "Queuing…" : "Post to selected platforms"}
              </button>
              <p className="text-xs text-muted-bone">
                Posting runs in the background. Platforms marked “off” aren’t connected yet — see Connections.
              </p>
            </div>
          </div>
        </div>
      )}

      {tab === "assets" && (
        <div>
          <div className="flex justify-end mb-6">
            <button
              data-testid="refresh-assets-btn"
              onClick={() => api.get("/admin/marketing/assets").then((r) => setAssets(r.data))}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
          {assets.length === 0 ? (
            <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              No assets yet. Generate images or videos from Compose.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {assets.map((a) => (
                <div
                  key={a.asset_id}
                  data-testid={`asset-card-${a.asset_id}`}
                  className="border flex flex-col"
                  style={{ ...borderStyle, background: "var(--rs-surface)" }}
                >
                  <div className="aspect-square overflow-hidden flex items-center justify-center" style={{ background: "var(--rs-surface-2, #0d0b0b)" }}>
                    {a.status === "generating" ? (
                      <div className="text-xs text-muted-bone animate-pulse">Sora is filming…</div>
                    ) : a.status === "failed" ? (
                      <div className="text-xs px-4 text-center" style={{ color: "var(--rs-ruby, #c05050)" }}>Failed: {a.error?.slice(0, 80)}</div>
                    ) : a.kind === "image" ? (
                      <img src={resolveUrl(a.url)} alt={a.prompt} className="w-full h-full object-cover" />
                    ) : (
                      <video src={resolveUrl(a.url)} className="w-full h-full object-cover" controls muted />
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-muted-bone">{a.kind}</span>
                      {statusPill(a.status)}
                    </div>
                    <p className="text-xs text-muted-bone line-clamp-2">{a.prompt}</p>
                    <div className="flex justify-between items-center">
                      <button
                        data-testid={`use-asset-${a.asset_id}`}
                        onClick={() => {
                          if (a.status !== "ready") return toast.error("Asset not ready");
                          setSelectedAsset(a);
                          setTab("compose");
                          toast.success("Asset attached to compose");
                        }}
                        className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
                      >
                        Use
                      </button>
                      <button
                        data-testid={`delete-asset-${a.asset_id}`}
                        onClick={() => deleteAsset(a.asset_id)}
                        className="text-muted-bone hover:text-bone"
                        aria-label="Delete asset"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "connections" && connections && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { key: "meta", label: "Meta — Facebook & Instagram", desc: "Posts to your Facebook Page and linked Instagram business account." },
            { key: "tiktok", label: "TikTok", desc: "Sends videos to your TikTok inbox as drafts to finish in-app." },
            { key: "x", label: "X (Twitter)", desc: "Posts tweets with optional images via your developer keys." },
          ].map(({ key, label, desc }) => {
            const c = connections[key];
            return (
              <div key={key} className="border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
                <div className="flex items-center justify-between">
                  <div className="overline">{label}</div>
                  {c.connected ? statusPill("posted") : (
                    <span className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-bone border" style={borderStyle}>
                      {c.configured ? "not connected" : "awaiting keys"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-bone leading-relaxed">{desc}</p>
                {c.connected ? (
                  <div className="space-y-3">
                    <div className="text-sm text-bone">
                      {key === "meta" && (c.account?.page_name || "Page connected")}
                      {key === "x" && `@${c.account?.handle || "connected"}`}
                      {key === "tiktok" && "TikTok connected"}
                    </div>
                    <button
                      data-testid={`disconnect-${key}-btn`}
                      onClick={() => disconnect(key)}
                      className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : key === "x" ? (
                  <div className="space-y-3">
                    {["api_key", "api_secret", "access_token", "access_token_secret"].map((f) => (
                      <input
                        key={f}
                        data-testid={`x-${f}-input`}
                        type="password"
                        value={xForm[f]}
                        onChange={(e) => setXForm((prev) => ({ ...prev, [f]: e.target.value }))}
                        placeholder={f.replace(/_/g, " ")}
                        className={inputCls}
                        style={borderStyle}
                      />
                    ))}
                    <button
                      data-testid="save-x-credentials-btn"
                      onClick={saveXCredentials}
                      disabled={savingX || Object.values(xForm).some((v) => !v.trim())}
                      className={btnPrimary}
                      style={{ background: "var(--rs-oxblood)" }}
                    >
                      {savingX ? "Verifying…" : "Connect X"}
                    </button>
                  </div>
                ) : c.configured ? (
                  <button
                    data-testid={`connect-${key}-btn`}
                    onClick={() => connectPlatform(key)}
                    className={`flex items-center gap-2 ${btnPrimary}`}
                    style={{ background: "var(--rs-oxblood)" }}
                  >
                    <Link2 size={14} /> Connect
                  </button>
                ) : (
                  <p className="text-xs" style={{ color: "var(--rs-ruby, #c05050)" }}>
                    API keys not configured yet — add them to the backend environment when your developer app is approved.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              data-testid="refresh-posts-btn"
              onClick={() => api.get("/admin/marketing/posts").then((r) => setPosts(r.data))}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
          {posts.length === 0 ? (
            <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              Nothing posted yet.
            </div>
          ) : (
            posts.map((p) => (
              <div
                key={p.post_id}
                data-testid={`post-history-${p.post_id}`}
                className="border p-6 flex flex-col sm:flex-row gap-4"
                style={{ ...borderStyle, background: "var(--rs-surface)" }}
              >
                {p.asset_url && (
                  p.asset_path?.endsWith(".mp4") ? (
                    <video src={resolveUrl(p.asset_url)} className="w-20 h-20 object-cover border" style={borderStyle} muted />
                  ) : (
                    <img src={resolveUrl(p.asset_url)} alt="" className="w-20 h-20 object-cover border" style={borderStyle} />
                  )
                )}
                <div className="flex-1">
                  <div className="text-xs text-muted-bone mb-2">{p.created_at?.slice(0, 16).replace("T", " ")}</div>
                  <p className="text-sm text-bone line-clamp-2 mb-3">{p.caption}</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(p.results || {}).map(([platform, r]) => (
                      <div key={platform} className="flex items-center gap-2 text-xs text-muted-bone">
                        <span className="uppercase tracking-widest">{platform}</span>
                        {statusPill(r.status)}
                        {r.error && <span className="text-[10px]" style={{ color: "var(--rs-ruby, #c05050)" }}>{r.error.slice(0, 60)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMarketing;
